import { DefiniteComponent, ReturnComponent } from '@/utils/reactTypes';
import { Box, Icon } from '@mui/material';
import React, { forwardRef } from 'react';

// Wrap a PNG or SVG image component with this to precisely control its sizing.
export const SvgIcon = forwardRef(
    (
        {
            height,
            width,
            padding = '0',
            fillColor = 'undefined',
            children,
            ...rest
        }: {
            height: string;
            width: string;
            padding?: string;
            fillColor?: string;
            children: DefiniteComponent;
        },
        ref,
    ): ReturnComponent => {
        // If anyone figures out how to specify sizing for images
        //  without this double <Box/> wrapper and all these CSS tricks, please
        //  tell me, but I couldn't figure it out. I tried a lot of things.
        return (
            // The <Icon/> element sets the correct font size, so '1em' matches
            //  the height and width of the other icons.
            <Icon>
                <Box
                    // The ref and {...rest} are to make wrapping with a tooltip work.
                    ref={ref}
                    {...rest}
                    sx={{
                        // Here we set the width, height, and internal padding we want.
                        width: width,
                        height: height,
                        padding: padding,
                    }}
                >
                    <Box
                        sx={{
                            // This makes the inner box have a defined size,
                            //  so the inner image can expand to it.
                            width: '100%',
                            height: '100%',
                            // This is needed to make the inner `position: absolute` work.
                            position: 'relative',
                            '& > *': {
                                // Nothing but absolute positioning seems to get
                                //  SVG images to be positioned correctly. You'd think
                                //  just `width: 100%` and `height: 100%` would work,
                                //  but SVG images get either vertically or horizontally
                                //  offset for absolutely no discernible reason.
                                //  Absolute positioning seems to fix this.
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                width: '100%',
                                height: '100%',
                            },
                            // If the specificity of this selector is too high, then
                            //  we are not able to override the fill color, like we
                            //  do in the navigation drawer to make the selected
                            //  page have a different fill color.
                            // That's why we use just '& g' instead of '& svg g' or
                            //  something else.
                            // Who designed CSS :(
                            '& g': {
                                fill: fillColor,
                            },
                        }}
                    >
                        {children}
                    </Box>
                </Box>
            </Icon>
        );
    },
);
SvgIcon.displayName = 'SvgIcon';
